import { useMutation, useQuery, useQueryClient, UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { QData } from "../../../types/basic-type-list";
import { TResponse, TResponseWithQData } from "../../../types/common";
import {
  IContract, IContractCreatePayload, IContractUpdatePayload,
  listContracts, createContract, updateContract, deleteContract,
} from "./callbacks";

export const useContractList = (
  qData: QData
): UseQueryResult<TResponseWithQData<IContract[]>, Error> & { refetchWithQuery: (p: Partial<QData>) => void } => {
  const [queryParams, setQueryParams] = useState<QData>({ ...qData });
  const queryResult = useQuery<TResponseWithQData<IContract[]>, Error>({
    queryKey: [API_ENDPOINTS.CONTRACT_LIST, { ...queryParams, total: null }],
    queryFn: async () => (await listContracts(queryParams)).data,
    select: (res) => ({ ...res, data: res.data.map((x) => ({ key: x.id, ...x })), qData: { ...queryParams, total: res.extra?.total } }),
    initialData: { data: [], success: true, message: "", code: 200, qData: { page: 1, limit: 20 }, extra: { page: 1, limit: 20, total: 0 } },
    staleTime: 20000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
  });
  return { ...queryResult, refetchWithQuery: (p) => setQueryParams(p as QData) };
};

export const useContractCreate = ({
  onSuccess, onError,
}: { onSuccess?: (res: TResponse<IContract>) => void; onError?: (e: any) => void }): UseMutationResult<TResponse<IContract>, unknown, IContractCreatePayload> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => (await createContract(data)).data,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.CONTRACT_LIST] });
      onSuccess?.(res);
    },
    onError,
  });
};

export const useContractUpdate = ({
  onSuccess, onError,
}: { onSuccess?: (res: TResponse<IContract>) => void; onError?: (e: any) => void }): UseMutationResult<TResponse<IContract>, unknown, IContractUpdatePayload> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => (await updateContract(data)).data,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.CONTRACT_LIST] });
      onSuccess?.(res);
    },
    onError,
  });
};

export const useContractDelete = ({
  onSuccess, onError,
}: { onSuccess?: () => void; onError?: (e: any) => void }): UseMutationResult<TResponse<null>, unknown, number> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await deleteContract(id)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.CONTRACT_LIST] });
      onSuccess?.();
    },
    onError,
  });
};
